import React, {forwardRef, useEffect, useMemo, useReducer} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
  loadWorkbooks,
  unsetWorkbooks,
} from '../../../store/actions/tableauActions';
import {blockResultSelector} from '../../../store/reducers/blockResultReducer';
import {getBlockSelector} from '../../../store/reducers/blockReducer';
import {IState} from '../../../store/reducers';
import {IItem} from '../../select';
import {BaseBlock, ContentActions} from '../types';
import {defaultContentActions} from '../constants';
import {ContentCard, ContentToolbar, NoIntegration, NoIntegrationCallout} from '../components';
import {integrationToSelectOption, shouldLoadResult} from '../utils';
import {
  useCreateBlock,
  useExecuteRequest,
  useGetIntegrations,
  useLoadResult,
  useUpdateBlockContentJson
} from '../hooks';
import {
  tableauContentReducer,
  initialTableauContentState,
} from './store';
import {
  setInit,
  setTableauContentForm,
  setTableauContentFormKeyValue,
  setTableauContentFormKeysValues,
} from './store/actions';
import {TableauContentOptions} from './types';
import {ACTION_TYPES, WORKBOOK_ACTIONS, tableauContentOptions} from './constants';
import {createDefaultContent} from './utils';
import {ExportOptions} from './ExportOptions';
import Result from './Result';
import TableauForm from './TableauForm';

interface TableauProps {
  content: BaseBlock;
  options?: TableauContentOptions;
  actions?: ContentActions;
}

export const Tableau = forwardRef(({
  content,
  options = tableauContentOptions,
  actions = defaultContentActions,
}: TableauProps, ref: any) => {
  options = useMemo((): TableauContentOptions => ({...tableauContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [contentState, contentDispatch] =
    useReducer(tableauContentReducer, initialTableauContentState);
  const uid = content.uid;
  const block = useSelector(getBlockSelector(uid as string));

  const tableauError = useSelector((state: IState) => state.tableau.error);
  const {integrations, hasIntegrations} = useGetIntegrations(content.type);
  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const result = useSelector(blockResultSelector(uid as string, content.result_id as string));
  const resultId: string | null = content.result_id || result?.key || null;
  const dispatch = useDispatch();

  useCreateBlock(content, createDefaultContent());
  useLoadResult(uid, resultId,
    options.loadResult !== undefined ? options.loadResult : shouldLoadResult(content.result_id, result)
  );

  useEffect(() => {
    /*
     * Initial load on edit
     */
    if (uid) {
      contentDispatch(setTableauContentForm(content));
    } else if (!uid && !!content.from_block_id) {
      /*
       * Comes from saved block
       */
      contentDispatch(setTableauContentForm(content));
    } else {
      contentDispatch(setInit(true));
    }

    return () => {
      dispatch(unsetWorkbooks());
    }
  }, []);

  const onExecute = () => {
    if (!contentState.form.integration_id) {
      return;
    }

    const block = {
      content_json: {
        ...contentState.form,
      }
    };
    executeRequest(block);
  };

  const onKeyValueChange = (key: string) => (value: any) => {
    updateBlockContentJson(key, value);
    contentDispatch(setTableauContentFormKeyValue(key, value));
  };

  const onKeysValuesChange = (keys: string[], values: any[]) => {
    /*
     * TODO: change this from [...keys], [...values] to {key: value}
     */
    updateBlockContentJson(keys, values);
    contentDispatch(setTableauContentFormKeysValues(keys, values));
  };

  const onIntegrationIdChange = (integrationId: string) => {
    /*
     * Update block on the backend;
     * Update block in useReducer;
     * Load workbooks and custom views for the integration;
     */
    dispatch(loadWorkbooks(uid as string, integrationId, undefined, true));

    const newContent = {
      'integration_id': integrationId,
      /*
       * If action is not available, leave it as the default action that was provided
       */
      'action': options?.showActions ? ACTION_TYPES.WORKBOOK_PDF : content?.action,
      'workbook_id': '',
      'workbook_name': '',
      'view_id': '',
      'view_name': '',
      'custom_view_id': '',
      'custom_view_name': '',
      'export_options': '',
    };
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onActionChange = (action: string) => {
    if (WORKBOOK_ACTIONS.includes(action)) {
      /*
       * If action is workbook related, reset view_id and custom_view_id.
       */
      const newContent = {
        'action': action,
        'view_id': '',
        'view_name': '',
        'custom_view_id': '',
        'custom_view_name': '',
        'export_options': '',
      }
      const keys = Object.keys(newContent);
      const values = Object.values(newContent);
      onKeysValuesChange(keys, values);
    } else {
      onKeyValueChange('action')(action);
    }
  };

  const onWorkbookChange = (workbook: IItem) => {
    /*
     * Reset view_id and custom_view_id.
     */
    const newContent = {
      'workbook_id': workbook.value,
      'workbook_name': workbook.label,
      'view_id': '',
      'view_name': '',
      'custom_view_id': '',
      'custom_view_name': '',
      'export_options': '',
    }
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onViewChange = (view: IItem) => {
    /*
     * Reset custom_view_id.
     */
    const newContent = {
      'view_id': view.value,
      'view_name': view.label,
      'custom_view_id': '',
      'custom_view_name': '',
      'export_options': '',
    }
    const keys = Object.keys(newContent);
    const values = Object.values(newContent);
    onKeysValuesChange(keys, values);
  };

  const onExportOptionsChange = (key: string) => (value: any) => {
    onKeyValueChange(`export_options.${key}`)(value);
  }

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? content.uid : null}
      disabled={!hasIntegrations || options.readOnly}
      noContentElement={hasIntegrations ? null : <NoIntegration
        contentId={uid as string}
        type={content.type}
        onDelete={actions.onDelete}
        showDelete={options.showDelete}
      />}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__tableau">
        <ContentToolbar
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          selected={contentState.form.integration_id}
          onSelect={onIntegrationIdChange}
          options={integrations.map(integrationToSelectOption)}
          updating={updating}
          selectLabel="Select Integration"
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={options.showSave}
          disabled={options.readOnly}
        />
        <div className="content-card__content__form">
          {
            !contentState.form.integration_id ? <NoIntegrationCallout /> : (
              <TableauForm
                uid={uid as string}
                integrationId={contentState.form.integration_id}
                action={contentState.form.action}
                workbookId={contentState.form.workbook_id}
                workbookName={contentState.form.workbook_name || ''}
                viewId={contentState.form.view_id || ''}
                viewName={contentState.form.view_name || ''}
                customViewId={contentState.form.custom_view_id || ''}
                customViewName={contentState.form.custom_view_name || ''}
                filters={contentState.form.filters || ''}
                resolution={contentState.form.resolution || ''} // Image resolution
                orientation={contentState.form.orientation || ''} // PDF orientation
                pageType={contentState.form.page_type || ''} // PDF page type
                vizHeight={contentState.form.viz_height || ''} // PDF viz-height
                vizWidth={contentState.form.viz_width || ''} // PDF viz-width
                onActionChange={onActionChange}
                onWorkbookChange={onWorkbookChange}
                onViewChange={onViewChange}
                onCustomViewChange={onKeyValueChange('custom_view_id')}
                onFiltersChange={onKeyValueChange('filters')}
                onResolutionChange={onKeyValueChange('resolution')}
                onOrientationChange={onKeyValueChange('orientation')}
                onPageTypeChange={onKeyValueChange('page_type')}
                onVizHeightChange={onKeyValueChange('viz_height')}
                onVizWidthChange={onKeyValueChange('viz_width')}
                disabled={options.readOnly}
                showActions={options.showActions}
                showCustomViews={options.showCustomViews}
              />
            )
          }
          {
            (!result && tableauError?.length > 0) ?
              <Result result={tableauError[0]} key="error"/> :
              <Result result={result} uid={uid as string} key="result"/>
          }
        </div>
      </div>
      <ExportOptions
        show={options.showExportOptions as boolean && contentState.form.integration_id !== null}
        block={block}
        action={contentState.form.action}
        onChange={onExportOptionsChange}
        disabled={options.readOnly}
        uid={uid as string}
        integrationId={contentState.form.integration_id}
        viewId={contentState.form.view_id || ''}
      />
    </ContentCard>
  )
});
